<script>
import BlockContent from '../UI/Content/BlockContent.vue'
import MessageList from '../Message/MessageList.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalMethodsMixin, globalVariablesMixin } from '../../Mixins/global'
import { platformMixins } from '../../Mixins/platform'
import ServiceList from '../Service/ServiceList.vue'

export default {
  components: { MessageList, BlockContent, ServiceList },
  mixins: [mixinComponent, globalVariablesMixin, globalMethodsMixin, platformMixins],

  props: {
    contractId: { type: Number, required: true },
    contract: { type: Object, required: true },
  },

  data() {
    return {
      services: {},
      messages: [],
    }
  },

  computed: {
    isClient() {
      return user.platforms.length === 0 && conf.connect_client
    },
    isPlatformClientAndLbp() {
      return user.isPlatformClient && this.isTheme('lbp')
    },
    sirenClientLabel() {
      if (this.contract?.client?.countryCode === 'ES') {
        return this.$t('entity.nif.client')
      }

      return this.$t('simulation.fields.sirenClient.label')
    },
    sirenSupplierLabel() {
      if (this.contract?.supplier?.countryCode === 'ES') {
        return this.$t('entity.nif.supplier')
      }

      return this.$t('simulation.fields.sirenSupplier.label')
    },
    guaranteeDeposit() {
      if (this.contract.finance.guaranteeDeposit === null) {
        return null
      }

      return Number(this.contract.finance.guaranteeDeposit)
    },
    serviceListContainerClass() {
      return {
        row: true,
        'd-none': this.availableServices.length === 0,
      }
    },
    availableServices() {
      return this.contract.finance.services.filter(
        (service) => service.serviceType.serviceCategory.display === true && service.active === true,
      )
    },
  },
  methods: {
    getDateLabel(date) {
      return date ? this.setDate(date, 'date') : '-'
    },
  },
}
</script>
<template>
  <div>
    <BlockContent>
      <div class="row">
        <div class="col-md-5 card" style="padding: 1em; margin: 0 auto; overflow-x: auto">
          <h4 class="mb-3">{{ $t('contract.information.contributor') }}</h4>
          <table class="table table-striped table-hover fs-16">
            <tbody>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.finished.client') }}
                </td>
                <td class="text-right">{{ contract.client ? contract.client.name : '-' }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">{{ sirenClientLabel }}</td>
                <td class="text-right">{{ contract.client ? contract.client.siren : '-' }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.finished.supplier') }}
                </td>
                <td class="text-right">{{ contract.supplier ? contract.supplier.name : '-' }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">{{ sirenSupplierLabel }}</td>
                <td class="text-right">{{ contract.supplier ? contract.supplier.siren : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="col-md-5 card" style="padding: 1em; margin: 0 auto; overflow-x: auto">
          <h4 class="mb-3" v-html="$t('contract.client.detail.menu')"></h4>
          <table class="table table-striped table-hover fs-16">
            <tbody>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.rent') }}
                </td>
                <td class="text-right">
                  {{ contract.rentInfo ? $n(Number(contract.rentInfo.rent ?? 0), 'currency') : '-' }}
                </td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.firstRent') }}
                </td>
                <td class="text-right">
                  {{
                    contract.finance
                      ? $n(Number(contract.finance.initialRent ?? contract.finance.markup ?? 0), 'currency')
                      : '-'
                  }}
                </td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.fields.period.label') }}
                </td>
                <td class="text-right">
                  {{
                    contract.finance && contract.finance.period
                      ? contract.finance.period.label
                        ? $t(contract.finance.period.label)
                        : contract.finance.period.name
                      : '-'
                  }}
                </td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.rentLeft') }}
                </td>
                <td class="text-right">{{ contract.nbRemainingRent }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.totalMonths') }}
                </td>
                <td class="text-right">{{ contract.finance ? contract.finance.duration : '-' }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.amountFinanced') }}
                </td>
                <td class="text-right">
                  {{ contract.finance ? $n(Number(contract.finance.amount ?? 0.0), 'currency') : '-' }}
                </td>
              </tr>
              <tr v-if="!isPlatformClientAndLbp">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.leftToPay') }}
                </td>
                <td class="text-right">{{ $n(Number(contract.previousRemainingRent ?? 0.0), 'currency') }}</td>
              </tr>
              <tr v-if="isTheme('olinn')">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.remainingAmount') }}
                </td>
                <td class="text-right">{{ $n(Number(contract.previousRemainingAmount ?? 0.0), 'currency') }}</td>
              </tr>
              <tr v-if="isTheme('olinn')">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.renewingBalance') }}
                </td>
                <td class="text-right">{{ $n(Number(contract.previousRenewingBalance ?? 0.0), 'currency') }}</td>
              </tr>
              <tr v-if="isTheme('olinn')">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.nonRenewingBalance') }}
                </td>
                <td class="text-right">{{ $n(Number(contract.previousNonRenewingBalance ?? 0.0), 'currency') }}</td>
              </tr>
              <tr v-if="isTheme('financia')">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.fields.finance.paymentMean.label') }}
                </td>
                <td v-if="contract.finance?.paymentMean?.code" class="text-right">
                  {{ $t('contract.fields.paymentMean.code.' + contract.finance.paymentMean.code) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="row">
        <div
          v-if="user.isBusinessMan || user.isAdmin"
          class="col-md-5 card"
          style="padding: 1em; margin: 2em auto; overflow-x: auto"
        >
          <h4 class="mb-3">{{ $t('contract.client.detail.other') }}</h4>
          <table class="table table-striped table-hover fs-16">
            <tbody>
              <tr v-if="guaranteeDeposit > 0">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.fields.finance.guaranteeDeposit.label') }}
                </td>
                <td class="text-right">{{ $n(guaranteeDeposit, 'currency') }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.rebuyValue') }} ({{ setDate(contract.repurchaseAt, 'date') }})
                </td>
                <td class="text-right">{{ $n(Number(contract.finance?.residualValue ?? 0), 'currency') }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.supplierPaidAt') }}
                </td>
                <td class="text-right">{{ setDate(contract.paidAt, 'date') }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.refinancerNumber') }}
                </td>
                <td class="text-right">{{ contract.refinancerNumber }}</td>
              </tr>
              <tr v-if="contract.formerContractNumber">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.formerContractNumber') }}
                </td>
                <td class="text-right">{{ contract.formerContractNumber }}</td>
              </tr>
              <tr v-if="contract.frameworkContractNumber">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.frameworkContractNumber') }}
                </td>
                <td class="text-right">{{ contract.frameworkContractNumber }}</td>
              </tr>
              <tr v-if="contract.agreementNumber && isTheme('financia')">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.fields.agreementNumber.label') }}
                </td>
                <td class="text-right">{{ contract.agreementNumber }}</td>
              </tr>
              <tr v-if="!isTheme('lbp')">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.fields.transferAmount.label') }}
                </td>
                <td class="text-right">{{ $n(Number(contract.transferAmount ?? 0.0), 'currency') }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.fields.rentInfo.purchaseRate.label') }}
                </td>
                <td class="text-right">{{ $n(Number(contract.rentInfo?.purchaseRate ?? 0.0)) }}</td>
              </tr>
              <tr v-if="isTheme('financia')">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.fields.managerSalesman.label') }}
                </td>
                <td class="text-right">{{ contract.managerSalesman?.name ?? '' }}</td>
              </tr>
              <tr v-if="isTheme('lbp')">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.fields.finance.contractMode.label') }}
                </td>
                <td class="text-right">{{ contract.finance.contractMode?.name ?? '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="!isPlatformClientAndLbp"
          class="col-md-5 card"
          style="padding: 1em; margin: 2em auto; overflow-x: auto"
        >
          <h4 class="mb-3">{{ $t('contract.information.nextPayment') }} :</h4>
          <table class="table table-striped table-hover fs-16">
            <tbody>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.startDate') }}
                </td>
                <td class="text-right">{{ getDateLabel(contract.startAt) }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.endDate') }}
                </td>
                <td class="text-right">{{ getDateLabel(contract.endAt) }}</td>
              </tr>
              <tr v-if="contract.prolongationEndAt">
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.prolongationEndAt') }}
                </td>
                <td class="text-right">{{ getDateLabel(contract.prolongationEndAt) }}</td>
              </tr>
              <tr>
                <td class="contract-detail__cell contract-detail__cell__title">
                  {{ $t('contract.information.refinancerProlongation') }}
                </td>
                <td class="text-right">{{ getBoolLabel(contract.refinancerProlongation) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="row">
        <div
          v-if="contract.finance && contract.finance.equipment"
          class="col card"
          style="padding: 1em; margin: 2em auto; overflow-x: auto"
        >
          <h4 class="mb-3">{{ $t('equipment.title') }}</h4>
          <table class="table table-striped table-hover fs-16">
            <thead>
              <tr>
                <th class="contract-detail__cell__title">{{ $t('main.supplier') }}</th>
                <th class="contract-detail__cell__title">{{ $t('equipment.fields.brand.label') }}</th>
                <th class="contract-detail__cell__title">{{ $t('equipment.fields.model.label') }}</th>
                <th class="contract-detail__cell__title">{{ $t('equipment.fields.serialNumber.label') }}</th>
                <th class="contract-detail__cell__title">{{ $t('equipment.fields.unitAmount.label') }}</th>
                <th v-if="!isPlatformClientAndLbp" class="contract-detail__cell__title">
                  {{ $t('equipment.fields.unitAmountSoft.label') }}
                </th>
                <th v-if="!isPlatformClientAndLbp" class="contract-detail__cell__title">
                  {{ $t('equipment.fields.unitAmountPrestation.label') }}
                </th>
                <th class="contract-detail__cell__title">{{ $t('equipment.fields.quantity.label') }}</th>
                <th class="contract-detail__cell__title">{{ $t('equipment.fields.amount.label') }}</th>
                <th class="contract-detail__cell__title">{{ $t('equipment.fields.year.label') }}</th>
                <th class="contract-detail__cell__title">{{ $t('equipment.fields.condition.label') }}</th>
                <th v-if="conf.theme_dir === 'ciscar'" class="contract-detail__cell__title">
                  {{ $t('equipment.fields.status.label') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ contract.finance.equipment.supplier ? contract.finance.equipment.supplier.name : '' }}</td>
                <td>{{ contract.finance.equipment.brand }}</td>
                <td>{{ contract.finance.equipment.model }}</td>
                <td class="text-right">{{ contract.finance.equipment.serialNumber }}</td>
                <td class="text-right">{{ $n(Number(contract.finance.equipment.unitAmount), 'currency') }}</td>
                <td v-if="!isPlatformClientAndLbp" class="text-right">
                  {{ $n(Number(contract.finance.equipment.unitAmountSoft), 'currency') }}
                </td>
                <td v-if="!isPlatformClientAndLbp" class="text-right">
                  {{ $n(Number(contract.finance.equipment.unitAmountPrestation), 'currency') }}
                </td>
                <td class="text-right">{{ contract.finance.equipment.quantity }}</td>
                <td class="text-right">{{ $n(Number(contract.finance.equipment.amount), 'currency') }}</td>
                <td class="text-right">{{ contract.finance.equipment.year }}</td>
                <td>{{ $t('equipmentCondition.choices.' + contract.finance.equipment.condition + '.label') }}</td>
                <td v-if="conf.theme_dir === 'ciscar'">{{ contract.finance.equipment.status }}</td>
              </tr>
              <tr
                v-if="contract.finance.equipment.equipments.length > 0"
                v-for="equipment in contract.finance.equipment.equipments"
                :key="equipment.id"
              >
                <td>{{ equipment.supplier ? equipment.supplier.name : '' }}</td>
                <td>{{ equipment.brand }}</td>
                <td>{{ equipment.model }}</td>
                <td class="text-right">{{ equipment.serialNumber }}</td>
                <td class="text-right">{{ $n(Number(equipment.unitAmount), 'currency') }}</td>
                <td v-if="!isPlatformClientAndLbp" class="text-right">
                  {{ $n(Number(equipment.unitAmountSoft), 'currency') }}
                </td>
                <td v-if="!isPlatformClientAndLbp" class="text-right">
                  {{ $n(Number(equipment.unitAmountPrestation), 'currency') }}
                </td>
                <td class="text-right">{{ equipment.quantity }}</td>
                <td class="text-right">{{ $n(Number(equipment.amount), 'currency') }}</td>
                <td class="text-right">{{ equipment.year }}</td>
                <td>{{ $t('equipmentCondition.choices.' + equipment.condition + '.label') }}</td>
                <td v-if="conf.theme_dir === 'ciscar'">{{ equipment.status }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="row" :class="serviceListContainerClass">
        <div class="col card" style="padding: 1em; margin: 2em auto; overflow-x: auto">
          <h4 class="mb-3">{{ $t('service.title') }}</h4>
          <ServiceList :services="availableServices" :contract-id="contract.id" :finance-id="contract.finance.id" />
        </div>
      </div>
      <div
        class="row"
        v-if="
          contract.supplierPurchaseOrders && contract.supplierPurchaseOrders.length > 0 && conf.theme_dir == 'ciscar'
        "
      >
        <div class="col card" style="padding: 1em; margin: 2em auto; overflow-x: auto">
          <h4 class="mb-3">{{ $t('purchaseOrder.title') }}</h4>
          <table class="table table-striped table-hover fs-16">
            <thead>
              <tr>
                <th>{{ $t('purchaseOrder.list.entity') }}</th>
                <th>{{ $t('purchaseOrder.list.number') }}</th>
                <th>{{ $t('purchaseOrder.list.status') }}</th>
                <th>{{ $t('purchaseOrder.list.sendingStatus') }}</th>
                <th>{{ $t('purchaseOrder.list.amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(purchaseorder, index) in contract.supplierPurchaseOrders" :key="index">
                <td>{{ purchaseorder.entityName }}</td>
                <td>{{ purchaseorder.number }}</td>
                <td>{{ $t('purchaseOrder.status.' + purchaseorder.status) }}</td>
                <td>{{ $t('purchaseOrder.status.' + purchaseorder.sendingStatus) }}</td>
                <td>{{ $n(Number(purchaseorder.amount), 'currency') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <MessageList
        v-if="!isClient && (!isPlatformClient || !isTheme('lbp'))"
        context="AppBundle\Entity\Contract"
        :context-id="contractId"
        api-call
      />
    </BlockContent>
  </div>
</template>
